import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, InjectionToken, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DkDialogRef, DkDialogService, DkMultiSelect, DkSelect, type DkSelectOption } from '@dkds/ng-components';
import { Observable, Subject, catchError, debounceTime, map, merge, of, switchMap, tap } from 'rxjs';

import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import {
  HubspotCompanyDto,
  HubspotConnectionDto,
  HubspotConnectionWrite,
  HubspotPipelineDto,
  HubspotPortalLinkDto,
} from '../../models/hubspot.dto';
import { RequestTypeDto } from '../../models/request.dto';
import { SiteListItemDto } from '../../models/site.dto';
import { HubspotService } from '../../services/hubspot.service';
import { ProjectService } from '../../services/project.service';
import { RequestService } from '../../services/request.service';

/** What the settings modal hands the dialog when it opens it. */
export interface HubspotSettingsData {
  orgId: string;
  organizationName: string;
  projectId: string;
  projectName: string;
  /** The organization's live HubSpot accounts. */
  portalLinks: HubspotPortalLinkDto[];
  connection: HubspotConnectionDto | null;
  /** Org owners and admins may disconnect the whole account from here. */
  canDisconnectAccount: boolean;
}

/** How the dialog closed, so the row knows to reload. Dismissing it closes with `undefined`. */
export type HubspotSettingsResult = 'saved' | 'removed' | 'account-disconnected';

export const HUBSPOT_SETTINGS_DATA = new InjectionToken<HubspotSettingsData>('HUBSPOT_SETTINGS_DATA');

/** The id the modal points the dialog's `aria-labelledby` at. */
export const HUBSPOT_SETTINGS_TITLE_ID = 'hubspotSettingsTitle';

/**
 * A company's domain reduced the way the backend reduces a site's address to its `domain`: no
 * scheme, path, port or `www.`, since HubSpot's may carry them. A subdomain stays a subdomain, so
 * `shop.clientco.com` does not match `clientco.com`, deliberately.
 */
export function bareHost(url: string | null | undefined): string {
  return (url ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .split(/[/?#]/)[0]
    .split(':')[0]
    .replace(/^www\./, '');
}

/** The write fields a 400 can be keyed by, besides `detail`. */
type HubspotField = 'portal_link_id' | 'company_ids' | 'pipeline_ids' | 'default_request_type_id';

const FIELDS: readonly HubspotField[] = ['portal_link_id', 'company_ids', 'pipeline_ids', 'default_request_type_id'];

/** A picker's list, or why it could not be read. */
interface Loaded<T> {
  items: T[];
  error: string;
}

/**
 * Where a project's HubSpot connection is set up: which companies it lists besides what its sites'
 * domains bring in, from which pipelines, and what kind its HubSpot Requests are filed as. There is
 * no requester to pick: the admin who saves it is the one they are filed under, never shown.
 *
 * Opened with `DkDialogService.open()`, which supplies the modal shell, the focus trap and Escape.
 */
@Component({
  selector: 'app-hubspot-settings-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, DatePipe, DkSelect, DkMultiSelect],
  templateUrl: './hubspot-settings-dialog.component.html',
  styleUrl: './hubspot-settings-dialog.component.scss',
})
export class HubspotSettingsDialogComponent {
  private readonly ref = inject<DkDialogRef<HubspotSettingsResult>>(DkDialogRef);
  private readonly hubspot = inject(HubspotService);
  private readonly projectApi = inject(ProjectService);
  private readonly requestService = inject(RequestService);
  private readonly dialogs = inject(DkDialogService);
  private readonly toastService = inject(ToastService);
  private readonly errorHandler = inject(ErrorHandlerService);

  protected readonly data = inject(HUBSPOT_SETTINGS_DATA);
  protected readonly connection = this.data.connection;
  protected readonly isEditing = this.connection !== null;
  protected readonly titleId = HUBSPOT_SETTINGS_TITLE_ID;

  protected readonly form = new FormGroup({
    portalLinkId: new FormControl(
      { value: this.connection?.portal_link_id ?? this.data.portalLinks[0]?.id ?? '', disabled: this.isEditing },
      { nonNullable: true },
    ),
    // Optional: most projects need no companies, since their sites' domains route tickets to them.
    companyIds: new FormControl<string[]>(this.connection?.companies.map((company) => company.id) ?? [], {
      nonNullable: true,
    }),
    // None picked means every pipeline.
    pipelineIds: new FormControl<string[]>(this.connection?.pipelines.map((pipeline) => pipeline.id) ?? [], {
      nonNullable: true,
    }),
    requestTypeId: new FormControl(this.connection?.default_request_type?.id ?? '', {
      nonNullable: true,
      validators: Validators.required,
    }),
  });

  /** The form's value as a signal, disabled controls included. */
  private readonly value = toSignal(this.form.valueChanges.pipe(map(() => this.form.getRawValue())), {
    initialValue: this.form.getRawValue(),
  });

  protected readonly portalLinkOptions = this.data.portalLinks.map((link) => ({
    id: link.id,
    label: `HubSpot account ${link.hub_id}`,
  }));

  // ── Companies ────────────────────────────────────────────────
  protected readonly companiesLoading = signal(false);
  protected readonly companiesError = signal('');
  private readonly companies = signal<HubspotCompanyDto[]>([]);
  /** Every company a search has returned, so a pick survives a later search that leaves it out. */
  private readonly seenCompanies = signal(new Map<string, HubspotCompanyDto>());
  private readonly typedCompanySearch$ = new Subject<string>();
  private readonly immediateCompanySearch$ = new Subject<string>();
  private lastCompanyQuery = '';

  protected readonly companyOptions = computed<DkSelectOption[]>(() => {
    const options = this.companies().map((company) => this.companyOption(company));
    // The multi-select draws a chip only for a value among its options, so every company already
    // picked stays one, whatever the latest search returned.
    const missing = this.value()
      .companyIds.filter((id) => !options.some((option) => option.value === id))
      .map((id) => {
        const seen = this.seenCompanies().get(id);
        if (seen) return this.companyOption(seen);
        // A listed company before any search has returned it: only its name is known.
        const listed = this.connection?.companies.find((company) => company.id === id);
        return { value: id, label: listed?.name ?? id };
      });
    return [...missing, ...options];
  });

  // ── Pipelines ────────────────────────────────────────────────
  protected readonly pipelinesLoading = signal(false);
  protected readonly pipelinesError = signal('');
  private readonly pipelines = signal<HubspotPipelineDto[]>([]);

  protected readonly pipelineOptions = computed<DkSelectOption[]>(() => {
    const options: DkSelectOption[] = this.pipelines().map((pipeline) => ({
      value: pipeline.id,
      label: pipeline.label,
    }));
    // A saved pipeline the list no longer has, or has not returned yet, keeps its chip.
    for (const saved of this.connection?.pipelines ?? []) {
      if (!options.some((option) => option.value === saved.id)) options.unshift({ value: saved.id, label: saved.name });
    }
    return options;
  });

  // ── Project data ─────────────────────────────────────────────
  protected readonly requestTypes = signal<RequestTypeDto[]>([]);
  protected readonly requestTypesError = signal('');
  private readonly sites = signal<SiteListItemDto[] | null>(null);

  /**
   * What tickets can match: the project's sites by domain. A ticket comes here when its company's
   * domain is exactly one of these, so a subdomain site is said to be one of its own.
   */
  protected readonly siteHint = computed<string[] | null>(() => {
    const sites = this.sites();
    if (!sites) return null;

    const namesByDomain = new Map<string, string[]>();
    for (const site of sites) {
      if (site.domain) namesByDomain.set(site.domain, [...(namesByDomain.get(site.domain) ?? []), site.site_name]);
    }
    if (!namesByDomain.size) {
      return ['No site in this project has an address, so tickets only come here for the companies you list.'];
    }

    const domains = [...namesByDomain.keys()];
    const lines = [
      `Tickets from companies on these domains come here: ${domains
        .map((domain) => `${domain} (${listOf(namesByDomain.get(domain)!)})`)
        .join(', ')}.`,
    ];
    const noAddress = sites.filter((site) => !site.domain).map((site) => site.site_name);
    if (noAddress.length) {
      lines.push(
        noAddress.length === 1
          ? `${noAddress[0]} has no address, so no ticket matches it.`
          : `${listOf(noAddress)} have no address, so no ticket matches them.`,
      );
    }
    const sub = domains.find((domain) => domains.some((parent) => domain.endsWith(`.${parent}`)));
    if (sub) {
      const parent = domains.find((domain) => sub.endsWith(`.${domain}`))!;
      lines.push(`Domains match exactly: a ticket from ${parent} doesn't match ${sub}.`);
    }
    return lines;
  });

  /**
   * Where each listed company's Requests will be filed, as far as this project's sites can tell.
   * A preview: one site match in another project beats the listing, and only the backend sees that.
   * Companies whose domain is not known yet, listed before any search returned them, are left out.
   */
  protected readonly companySiteHints = computed<string[]>(() => {
    const sites = this.sites();
    if (!sites) return [];
    return this.value()
      .companyIds.map((id) => this.seenCompanies().get(id))
      .filter((company): company is HubspotCompanyDto => !!company)
      .map((company) => {
        const domain = company.domain ? bareHost(company.domain) : '';
        const names = domain ? sites.filter((site) => site.domain === domain).map((site) => site.site_name) : [];
        return names.length
          ? `Requests from ${company.name} will be filed under ${listOf(names)}.`
          : `${company.name}'s Requests come here with no site, unless another project has a site on its domain.`;
      });
  });

  /** The listed companies HubSpot deleted or merged away since the connection was last saved. */
  protected readonly removedCompanyNotices = (this.connection?.removed_companies ?? []).map((company) =>
    company.reason === 'company_merged'
      ? `${company.name} was removed: merged in HubSpot.`
      : `${company.name} was removed: deleted in HubSpot.`,
  );

  // ── Broken connection ────────────────────────────────────────
  /** Any save resumes a broken connection, even one that changes nothing. */
  protected readonly brokenMessage = this.connection?.is_broken
    ? 'New HubSpot tickets are on hold. Save the connection to resume them.'
    : '';

  // ── Saving ───────────────────────────────────────────────────
  protected readonly saving = signal(false);
  protected readonly removing = signal(false);
  protected readonly busy = computed(() => this.saving() || this.removing());
  protected readonly fieldErrors = signal<Partial<Record<HubspotField, string>>>({});
  /** A 400's `detail`, or a 502: shown in an alert above the footer, with the form kept. */
  protected readonly saveError = signal('');

  protected readonly companyInvalid = computed(() => !!this.fieldErrors().company_ids);
  protected readonly pipelineInvalid = computed(() => !!this.fieldErrors().pipeline_ids);

  protected readonly canSave = computed(() => {
    const value = this.value();
    // No company is needed: a project's sites route its tickets.
    if (this.busy() || !value.requestTypeId) return false;
    return !this.isEditing || !!this.connection?.is_broken || Object.keys(this.changes()).length > 0;
  });

  constructor() {
    merge(this.immediateCompanySearch$, this.typedCompanySearch$.pipe(debounceTime(300)))
      .pipe(
        tap((q) => {
          this.lastCompanyQuery = q;
          this.companiesLoading.set(true);
          this.companiesError.set('');
        }),
        // The latest search only: a slow answer to an older query must not replace a newer one.
        switchMap((q) => this.load(this.hubspot.searchCompanies(this.data.orgId, this.portalLinkId(), q))),
        takeUntilDestroyed(),
      )
      .subscribe(({ items, error }) => {
        this.companiesLoading.set(false);
        this.companiesError.set(error);
        this.companies.set(items);
        if (items.length) {
          this.seenCompanies.update((seen) => new Map([...seen, ...items.map((c) => [c.id, c] as const)]));
        }
      });

    this.form.controls.portalLinkId.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      // Companies and pipelines belong to an account, so a pick in another one means nothing here.
      this.form.patchValue({ companyIds: [], pipelineIds: [] });
      this.loadPickers();
    });

    this.loadPickers();
    this.loadProjectData();
  }

  protected onCompanySearch(q: string): void {
    this.typedCompanySearch$.next(q);
  }

  protected retryCompanies(): void {
    this.immediateCompanySearch$.next(this.lastCompanyQuery);
  }

  protected loadPipelines(): void {
    this.pipelinesLoading.set(true);
    this.pipelinesError.set('');
    this.load(this.hubspot.getPipelines(this.data.orgId, this.portalLinkId())).subscribe(({ items, error }) => {
      this.pipelinesLoading.set(false);
      this.pipelinesError.set(error);
      this.pipelines.set(items);
    });
  }

  protected close(): void {
    this.ref.close();
  }

  protected save(): void {
    if (!this.canSave()) return;

    const value = this.value();
    const body: HubspotConnectionWrite = this.isEditing
      ? this.changes()
      : {
          portal_link_id: value.portalLinkId,
          company_ids: value.companyIds,
          pipeline_ids: value.pipelineIds,
          default_request_type_id: value.requestTypeId,
        };

    this.saving.set(true);
    this.fieldErrors.set({});
    this.saveError.set('');
    this.hubspot.saveProjectConnection(this.data.projectId, body, this.isEditing).subscribe({
      next: () => {
        this.saving.set(false);
        if (this.isEditing) {
          this.toastService.success({ message: 'HubSpot settings updated.' });
        } else {
          this.toastService.success({
            title: 'Project connected to HubSpot.',
            message: 'Only new HubSpot tickets become Requests.',
          });
        }
        this.ref.close('saved');
      },
      error: (err: HttpErrorResponse) => {
        this.saving.set(false);
        this.showSaveError(err);
      },
    });
  }

  protected async removeFromProject(): Promise<void> {
    const confirmed = await this.dialogs.confirm({
      title: `Remove HubSpot from ${this.data.projectName}?`,
      message:
        "Requests already created from HubSpot stay in this project, but stop syncing. New HubSpot tickets won't come in.",
      confirmLabel: 'Remove',
      tone: 'destructive',
    });
    if (!confirmed) return;

    this.removing.set(true);
    this.hubspot.removeProjectConnection(this.data.projectId).subscribe({
      next: () => {
        this.removing.set(false);
        this.toastService.success({ message: 'This project is no longer connected to HubSpot.' });
        this.ref.close('removed');
      },
      error: (err: HttpErrorResponse) => {
        this.removing.set(false);
        this.toastService.error({ message: this.messageOf(err) });
      },
    });
  }

  protected async disconnectAccount(): Promise<void> {
    const linkId = this.portalLinkId();
    const hubId = this.data.portalLinks.find((link) => link.id === linkId)?.hub_id ?? this.connection?.hub_id ?? '';
    const confirmed = await this.dialogs.confirm({
      title: `Disconnect HubSpot from ${this.data.organizationName}?`,
      message: `Every project connected to HubSpot account ${hubId} stops syncing. Requests already created stay. Reconnecting later won't relink them.`,
      confirmLabel: 'Disconnect',
      tone: 'destructive',
    });
    if (!confirmed) return;

    this.removing.set(true);
    this.hubspot.disconnectPortalLink(this.data.orgId, linkId).subscribe({
      next: () => {
        this.removing.set(false);
        this.toastService.success({ message: 'HubSpot disconnected.' });
        this.ref.close('account-disconnected');
      },
      error: (err: HttpErrorResponse) => {
        this.removing.set(false);
        this.toastService.error({ message: this.messageOf(err) });
      },
    });
  }

  private portalLinkId(): string {
    return this.value().portalLinkId;
  }

  private loadPickers(): void {
    this.companies.set([]);
    this.immediateCompanySearch$.next('');
    this.loadPipelines();
  }

  private loadProjectData(): void {
    const projectId = this.data.projectId;

    this.requestService.getRequestTypes(projectId).subscribe({
      next: (res) => {
        const types = (res.data ?? []).filter((type) => type.is_active);
        this.requestTypes.set(types);
        const control = this.form.controls.requestTypeId;
        if (!this.isEditing && !control.value) {
          const technical = types.find((type) => type.name.trim().toLowerCase() === 'technical');
          if (technical) control.setValue(technical.id);
        } else if (control.value && !types.some((type) => type.id === control.value)) {
          control.setValue('');
        }
      },
      error: (err: HttpErrorResponse) => this.requestTypesError.set(this.messageOf(err)),
    });

    this.projectApi.getProjectSites(projectId).subscribe({
      next: (res) => this.sites.set(res.data ?? []),
      // Without the sites there is nothing to hint, and nothing the reader has to act on.
      error: () => this.sites.set(null),
    });
  }

  /** A picker's request as a list or an error message, never a thrown error. */
  private load<T>(request: Observable<{ data: T[] }>): Observable<Loaded<T>> {
    return request.pipe(
      map((res) => ({ items: res.data ?? [], error: '' })),
      catchError((err: HttpErrorResponse) => of({ items: [] as T[], error: this.messageOf(err) })),
    );
  }

  private companyOption(company: HubspotCompanyDto): DkSelectOption {
    const label = company.domain ? `${company.name} · ${company.domain}` : company.name;
    const takenBy = company.connected_project;
    // The company this project already uses comes back attached to this very project.
    return takenBy && takenBy.id !== this.data.projectId
      ? { value: company.id, label: `${label} — used by ${takenBy.name}`, disabled: true }
      : { value: company.id, label };
  }

  /** What changed against the saved connection: a PATCH sends nothing else. */
  private changes(): HubspotConnectionWrite {
    const connection = this.connection;
    if (!connection) return {};
    const value = this.value();
    const body: HubspotConnectionWrite = {};
    // `company_ids` replaces the whole list, so a change in it sends all of it.
    if (
      !sameIds(
        value.companyIds,
        connection.companies.map((company) => company.id),
      )
    ) {
      body.company_ids = value.companyIds;
    }
    if (
      !sameIds(
        value.pipelineIds,
        connection.pipelines.map((pipeline) => pipeline.id),
      )
    ) {
      body.pipeline_ids = value.pipelineIds;
    }
    if (value.requestTypeId !== (connection.default_request_type?.id ?? '')) {
      body.default_request_type_id = value.requestTypeId;
    }
    return body;
  }

  private showSaveError(err: HttpErrorResponse): void {
    const message = this.messageOf(err);
    if (err.status === 400) {
      const errors: Record<string, unknown> = err.error?.errors ?? {};
      const fieldErrors: Partial<Record<HubspotField, string>> = {};
      for (const field of FIELDS) {
        const text = errorText(errors[field]);
        if (text) fieldErrors[field] = text;
      }
      this.fieldErrors.set(fieldErrors);
      const detail = errorText(errors['detail']);
      // A 400 that names no field still has to say something.
      this.saveError.set(detail || (Object.keys(fieldErrors).length ? '' : message));
      // `detail` can mean another save took one of the companies meanwhile: re-read who lists what.
      if (detail) this.retryCompanies();
    } else if (err.status === 502) {
      // HubSpot is unreachable or refuses the account: the form stays, so the reader can retry.
      this.saveError.set(message);
    } else {
      this.toastService.error({ message });
    }
  }

  /**
   * The backend's own message when it sent one. `getUserFriendlyErrorMessage` replaces every 5xx
   * with a generic line, which would hide HubSpot's 502 reason, and it throws on a 400 that has
   * no `email` error, so it is only the fallback.
   */
  private messageOf(err: HttpErrorResponse): string {
    const message: unknown = err.error?.message;
    if (typeof message === 'string' && message) return message;
    return err.status === 400 ? 'Check the fields and try again.' : this.errorHandler.getUserFriendlyErrorMessage(err);
  }
}

/** A validation error as one line, whether the backend sent a string or a list of them. */
function errorText(value: unknown): string {
  if (Array.isArray(value)) return value.map(String).join(' ');
  return typeof value === 'string' ? value : '';
}

/** Whether two id lists hold the same ids, in any order. */
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/** Names as a sentence: "A", "A and B", "A, B and C". */
function listOf(names: readonly string[]): string {
  return new Intl.ListFormat('en', { type: 'conjunction' }).format(names);
}
